// ----- Tracker row 14 — "someone else changed this" -----
//
// Shown when a save is refused because the record changed since it was opened.
// The editor stays open with the user's edits intact; nothing is overwritten
// automatically. Two ways forward: reload (taking their changes, losing these
// edits) or review the differences field by field first.

import { useState, type ReactElement } from 'react';
import type { FieldDifference } from '@/lib/concurrency';

export interface SaveConflictBannerProps {
  /** e.g. "This profile was changed by someone else." */
  readonly what: string;
  readonly differences: readonly FieldDifference[];
  readonly onReload: () => void;
  /** data-testid prefix, so several banners can coexist on one page. */
  readonly testId: string;
}

export function SaveConflictBanner({
  what,
  differences,
  onReload,
  testId,
}: SaveConflictBannerProps): ReactElement {
  const [reviewing, setReviewing] = useState(false);
  return (
    <div
      role="alert"
      data-testid={testId}
      style={{
        border: '1px solid var(--amber, #E0A800)',
        background: 'var(--amber-bg, #FFF8E1)',
        borderRadius: 6,
        padding: '10px 12px',
        marginBottom: 12,
        fontSize: 13,
        lineHeight: 1.5,
        color: 'var(--text)',
      }}
    >
      <div>
        <strong>{what}</strong> Reload to see their changes. Your edits are still in the form and
        have not been saved.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid={`${testId}-reload`}
          onClick={onReload}
          style={{
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '5px 10px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Reload (lose my edits)
        </button>
        <button
          type="button"
          data-testid={`${testId}-review`}
          aria-expanded={reviewing}
          onClick={() => setReviewing((r) => !r)}
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '5px 10px',
            fontSize: 12,
            cursor: 'pointer',
            color: 'var(--text)',
          }}
        >
          {reviewing ? 'Hide differences' : 'Review differences'}
        </button>
      </div>
      {reviewing && (
        <div data-testid={`${testId}-differences`} style={{ marginTop: 10 }}>
          {differences.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              The record was saved by someone else, but none of the fields shown here differ from
              what you loaded (for example, only its timestamp moved). Reload to continue from the
              saved version.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '4px 6px' }}>Field</th>
                  <th style={{ padding: '4px 6px' }}>Your value</th>
                  <th style={{ padding: '4px 6px' }}>Current value</th>
                </tr>
              </thead>
              <tbody>
                {differences.map((d) => (
                  <tr
                    key={d.field}
                    data-testid={`${testId}-diff-${d.field}`}
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{d.label}</td>
                    <td style={{ padding: '4px 6px', wordBreak: 'break-word' }}>{d.yours}</td>
                    <td style={{ padding: '4px 6px', wordBreak: 'break-word' }}>{d.current}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
